import React, { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, Box, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Button, Typography, Tooltip, IconButton, DialogActions } from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { QuestionDTO, QuestionType, ApplicantRoleType, MatchingImportance } from "../../../../types/ApplicationQuestion";
import { GridQuestionOptions, StandardQuestionOptions } from "../../../../types/ApplicationQuestion";

// Edit Question Modal Props
interface EditQuestionModalProps {
    open: boolean;
    question: QuestionDTO | null;
    questions: QuestionDTO[];
    onClose: () => void;
    onSave: (question: QuestionDTO) => void;
}

// Edit Question Modal Component
const EditQuestionModal: React.FC<EditQuestionModalProps> = ({ open, question, questions, onClose, onSave }) => {
    const [editedQuestion, setEditedQuestion] = useState<QuestionDTO | null>(question);

    // Get potential matching questions of opposite role that are not already matched
    const getMatchingQuestions = useCallback(() => {
        if (!editedQuestion || editedQuestion.for_role === ApplicantRoleType.BOTH) return [];
        const oppositeRole = editedQuestion.for_role === ApplicantRoleType.MENTOR ? ApplicantRoleType.MENTEE : ApplicantRoleType.MENTOR;
        return questions
            .filter(q => q.for_role === oppositeRole && !q.matching_question_id && q.id !== editedQuestion.id)
            .map(q => ({
                value: q.id,
                label: q.question_text
            }));
    }, [editedQuestion, questions]);

    // Update local state when question prop changes
    useEffect(() => {
        setEditedQuestion(question);
    }, [question]);

    const handleSave = () => {
        if (editedQuestion) {
            onSave(editedQuestion);
        }
    };

    if (!editedQuestion) return null;

    // Determine if this is a new question (empty question text) or an existing one
    const isNewQuestion = !editedQuestion.question_text;

    // Helper function to check if question is a grid type
    const isGridQuestion = (questionType: QuestionType) => {
        return questionType === QuestionType.MULTIPLE_CHOICE_GRID || questionType === QuestionType.SINGLE_CHOICE_GRID;
    };

    // Helper function to get grid options
    const getGridOptions = (): GridQuestionOptions => {
        if (editedQuestion.options && isGridQuestion(editedQuestion.question_type)) {
            const gridOptions = editedQuestion.options as GridQuestionOptions;
            return {
                rows: gridOptions.rows || [],
                columns: gridOptions.columns || []
            };
        }
        return { rows: [], columns: [] };
    };

    // Helper function to get standard options
    const getStandardOptions = (): string[] => {
        if (editedQuestion.options && !isGridQuestion(editedQuestion.question_type)) {
            const standardOptions = editedQuestion.options as StandardQuestionOptions;
            return Object.values(standardOptions);
        }
        return [];
    };

    // Convert options dictionary to array for UI display (for non-grid questions)
    const optionsArray = getStandardOptions();
    const gridOptions = getGridOptions();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isNewQuestion ? 'Add Question' : 'Edit Question'}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Question Text"
                        fullWidth
                        value={editedQuestion.question_text || ''}
                        onChange={(e) => setEditedQuestion({ ...editedQuestion, question_text: e.target.value })}
                        sx={{ mb: 3 }}
                    />

                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="question-type-label">Question Type</InputLabel>
                        <Select
                            labelId="question-type-label"
                            value={editedQuestion.question_type || ''}
                            label="Question Type"
                            onChange={(e) => setEditedQuestion({ ...editedQuestion, question_type: e.target.value as QuestionType })}
                        >
                            <MenuItem value={QuestionType.SHORT_ANSWER}>Short Answer</MenuItem>
                            <MenuItem value={QuestionType.PARAGRAPH}>Paragraph</MenuItem>
                            <MenuItem value={QuestionType.SINGLE_CHOICE}>Single Choice (Radio)</MenuItem>
                            <MenuItem value={QuestionType.MULTIPLE_CHOICE}>Multiple Choice (Checkbox)</MenuItem>
                            <MenuItem value={QuestionType.DROPDOWN}>Dropdown</MenuItem>
                            <MenuItem value={QuestionType.SINGLE_CHOICE_GRID}>Single-Choice Grid</MenuItem>
                            <MenuItem value={QuestionType.MULTIPLE_CHOICE_GRID}>Multi-Choice Grid</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editedQuestion.required || false}
                                onChange={(e) => setEditedQuestion({ ...editedQuestion, required: e.target.checked })}
                            />
                        }
                        label="Required"
                    />

                    {/* Question role targeting */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Apply To</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={editedQuestion.for_role || ''}
                                onChange={(e) => setEditedQuestion({
                                    ...editedQuestion,
                                    for_role: e.target.value as ApplicantRoleType,
                                    matching_question_id: e.target.value === ApplicantRoleType.BOTH ? undefined : editedQuestion.matching_question_id
                                })}
                            >
                                <MenuItem value={ApplicantRoleType.BOTH}>Both Roles (Mentor & Mentee)</MenuItem>
                                <MenuItem value={ApplicantRoleType.MENTOR}>Mentors Only</MenuItem>
                                <MenuItem value={ApplicantRoleType.MENTEE}>Mentees Only</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Matching importance */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Matching Importance</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={editedQuestion.matching_importance || ''}
                                onChange={(e) => setEditedQuestion({
                                    ...editedQuestion,
                                    matching_importance: e.target.value ? e.target.value as MatchingImportance : undefined
                                })}
                            >
                                <MenuItem value={MatchingImportance.NOT_USED}>
                                    <em>Not used for matching</em>
                                </MenuItem>
                                <MenuItem value={MatchingImportance.LOW}>Low</MenuItem>
                                <MenuItem value={MatchingImportance.MEDIUM}>Medium</MenuItem>
                                <MenuItem value={MatchingImportance.HIGH}>High</MenuItem>
                                <MenuItem value={MatchingImportance.CRITICAL}>Critical</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Matching question selector */}
                    {editedQuestion.for_role !== ApplicantRoleType.BOTH && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Matching Question</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={editedQuestion.matching_question_id || ''}
                                    onChange={(e) => setEditedQuestion({ ...editedQuestion, matching_question_id: e.target.value || undefined })}
                                >
                                    <MenuItem value="">
                                        <em>None (or create new)</em>
                                    </MenuItem>
                                    {getMatchingQuestions().map((q) => (
                                        <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                Select an existing question from the other role that this should be matched with,
                                or leave empty to create a new matching question automatically.
                            </Typography>
                        </Box>
                    )}

                    {(editedQuestion.question_type === QuestionType.MULTIPLE_CHOICE ||
                        editedQuestion.question_type === QuestionType.SINGLE_CHOICE ||
                        editedQuestion.question_type === QuestionType.DROPDOWN
                    ) && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                                {optionsArray.map((option: string, index: number) => (
                                    <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                                        <TextField
                                            fullWidth
                                            value={option}
                                            onChange={(e) => {
                                                const newOptionsArray = [...optionsArray];
                                                newOptionsArray[index] = e.target.value;
                                                // Convert back to dictionary
                                                const newOptions: Record<string, string> = {};
                                                newOptionsArray.forEach((val, idx) => {
                                                    newOptions[`option_${idx}`] = val;
                                                });
                                                setEditedQuestion({ ...editedQuestion, options: newOptions });
                                            }}
                                        />
                                        <Tooltip title="Remove option">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const newOptionsArray = optionsArray.filter((_row: string, i: number) => i !== index);
                                                    // Convert back to dictionary
                                                    const newOptions: Record<string, string> = {};
                                                    newOptionsArray.forEach((val, idx) => {
                                                        newOptions[`option_${idx}`] = val;
                                                    });
                                                    setEditedQuestion({ ...editedQuestion, options: newOptions });
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                                <Tooltip title="Add a new answer option">
                                    <Button
                                        variant="text"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            const newOptionsArray = [...optionsArray, ''];
                                            // Convert back to dictionary
                                            const newOptions: Record<string, string> = {};
                                            newOptionsArray.forEach((val, idx) => {
                                                newOptions[`option_${idx}`] = val;
                                            });
                                            setEditedQuestion({ ...editedQuestion, options: newOptions });
                                        }}
                                        sx={{ mt: 1 }}
                                    >
                                        Add Option
                                    </Button>
                                </Tooltip>
                            </Box>
                        )}

                    {/* Grid Question Options (Rows and Columns) */}
                    {isGridQuestion(editedQuestion.question_type) && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>Grid Options</Typography>

                            {/* Rows Section */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>Rows</Typography>
                                {gridOptions.rows.map((row: string, index: number) => (
                                    <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Row label"
                                            value={row}
                                            onChange={(e) => {
                                                const newRows = [...gridOptions.rows];
                                                newRows[index] = e.target.value;
                                                const newGridOptions: GridQuestionOptions = {
                                                    rows: newRows,
                                                    columns: gridOptions.columns
                                                };
                                                setEditedQuestion({ ...editedQuestion, options: newGridOptions });
                                            }}
                                        />
                                        <Tooltip title="Remove row">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const newRows = gridOptions.rows.filter((_row: string, i: number) => i !== index);
                                                    const newGridOptions: GridQuestionOptions = {
                                                        rows: newRows,
                                                        columns: gridOptions.columns
                                                    };
                                                    setEditedQuestion({ ...editedQuestion, options: newGridOptions });
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        const newRows = [...gridOptions.rows, ''];
                                        const newGridOptions: GridQuestionOptions = {
                                            rows: newRows,
                                            columns: gridOptions.columns
                                        };
                                        setEditedQuestion({ ...editedQuestion, options: newGridOptions });
                                    }}
                                >
                                    Add Row
                                </Button>
                            </Box>

                            {/* Columns Section */}
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>Columns</Typography>
                                {gridOptions.columns.map((column: string, index: number) => (
                                    <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Column label"
                                            value={column}
                                            onChange={(e) => {
                                                const newColumns = [...gridOptions.columns];
                                                newColumns[index] = e.target.value;
                                                const newGridOptions: GridQuestionOptions = {
                                                    rows: gridOptions.rows,
                                                    columns: newColumns
                                                };
                                                setEditedQuestion({ ...editedQuestion, options: newGridOptions });
                                            }}
                                        />
                                        <Tooltip title="Remove column">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const newColumns = gridOptions.columns.filter((_row: string, i: number) => i !== index);
                                                    const newGridOptions: GridQuestionOptions = {
                                                        rows: gridOptions.rows,
                                                        columns: newColumns
                                                    };
                                                    setEditedQuestion({ ...editedQuestion, options: newGridOptions });
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        const newColumns = [...gridOptions.columns, ''];
                                        const newGridOptions: GridQuestionOptions = {
                                            rows: gridOptions.rows,
                                            columns: newColumns
                                        };
                                        setEditedQuestion({ ...editedQuestion, options: newGridOptions });
                                    }}
                                >
                                    Add Column
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Tooltip title="Cancel editing">
                    <Button onClick={onClose} color="primary">
                        Cancel
                    </Button>
                </Tooltip>
                <Tooltip title="Save changes">
                    <Button onClick={handleSave} color="primary" variant="contained">
                        Save
                    </Button>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default EditQuestionModal;